<!--
  Created 01.11.2023
  Map entities component for adding, editing and selecting entities for a destination
  Used in both Destination and Proposal
-->
<template>
  <div class="flex h-[calc(100vh-178px)] flex-row">
    <!-- Destination entities -->
    <div class="basis-1/4 flex-col overflow-y-scroll text-gray-800">
      <!-- Search bar -->
      <div class="p-4">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search entities..."
          class="w-full rounded-md border border-gray-300 p-2">
      </div>
      <!-- Entities list -->
      <div
        v-if="filteredEntitiesGrouped.length > 0"
        class="grid grid-cols-1">
        <Accordion
          v-for="entity in filteredEntitiesGrouped"
          :key="'Accordion-' + entity.id"
          :state="entity.active"
          class="first:border-t first:border-gray-200"
          @toggle="(state) => toggleAccordions(state, entity)">
          <template #title>
            <component
              :is="entity.icon"
              class="size-10 p-1.5  pb-2"
              aria-hidden="true" />
            <h2 class="ml-2 text-left text-lg">
              {{ entity.title }} ({{ entity.list.length ?? '0' }})
            </h2>
          </template>
          <template
            #content>
            <div v-if="entity.list !== undefined && entity.list.length > 0">
              <div
                v-for="listItem in entity.list"
                :key="'AccordionContent-' + entity.id + '--' + listItem?.id"
                class="group cursor-pointer border-b
                      border-gray-200 bg-gray-100 hover:bg-cyan-50"
                :class="{'active': listItem?.selected}"
                @click="selectListItem(entity, listItem)">
                <div class="relative border-l-2 p-3 pl-4 group-[&.active]:border-cyan-600">
                  <small v-if="destinations.length > 1">{{ listItem?.parent?.name }}</small>
                  <h3
                    class="text-sm font-semibold group-[&.active]:text-cyan-600">
                    {{ listItem?.dbSelectedVersion?.texts[currentLanguage]?.name ?? listItem?.place?.name }}
                    <small
                      v-if="(listItem?.place?.location_disabled || listItem?.position_disabled) && !(listItem?.place?.global || listItem?.global)"
                      class="text-blue-500">({{ destination.name }} area)</small>
                    <small
                      v-else-if="(listItem?.place?.location_disabled || listItem?.position_disabled) && (listItem?.place?.global || listItem?.global)"
                      class="text-yellow-500">(global)</small>
                  </h3>
                  <small class="-mt-0.5 block text-xs text-gray-500">
                    {{ (entity?.titleSingle ?? '') +
                      (listItem?.dbSelectedVersion?.data?.rating ?
                        ', ' + listItem?.dbSelectedVersion?.data?.rating + '/5 (' + listItem?.dbSelectedVersion?.data?.user_ratings_total + ' reviews)' :
                        '') }}
                  </small>
                  <p class="mt-1 block text-xs">
                    {{ stringLimit(listItem?.dbSelectedVersion?.texts[currentLanguage]?.description, 82) }}
                  </p>
                  <Tooltip
                    class="!absolute right-1 top-2.5"
                    content-class="mt-2 bg-gray-900 text-gray-100 w-48 p-2 rounded opacity-95"
                    align="right">
                    <template #trigger>
                      <ExclamationCircleIcon
                        v-if="listItem?.within50km === false"
                        class="size-8 p-1  pb-2 text-red-700"
                        title="This item is more than 50 km away from city center."
                        aria-hidden="true"
                        aria-label="This item is more than 50 km away from city center." />
                    </template>
                    <template #content>
                      <p class="text-xs">
                        This item is more than 50 km away from city center.
                      </p>
                    </template>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div
              v-else
              class="border-b border-gray-200
                      bg-gray-100">
              <p class="p-3 pl-4 text-sm italic">
                There are no {{ entity.title }} registered <span v-if="entity.title !== 'Globals'">for {{ destination.name }}</span>
              </p>
            </div>
          </template>
        </Accordion>
      </div>
      <div
        v-else-if="searchQuery.length > 0"
        class="px-4 pt-2 text-center text-sm italic">
        Found no entities matching your search. Try another search term.
      </div>
      <div class="m-8 flex flex-row items-center justify-center">
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-black py-1 pl-2 pr-4 text-sm font-medium
                text-black shadow-sm hover:border-purple-700 hover:bg-purple-700 hover:text-white focus:outline-none
                focus:ring-2 focus:ring-purple-800 focus:ring-offset-2"
          @click="toggleModal">
          <PlusSmallIcon
            class="size-6"
            aria-hidden="true" /> <span class="ml-1">Add more entities</span>
        </button>
      </div>
    </div>
    <!-- Destination map -->
    <div class="flex basis-3/4 flex-row border-l border-gray-200">
      <!-- Map of destination -->
      <div
        class="relative h-full "
        :class="(selectedEntityId && selectedListItemId) ? 'w-1/2' : 'w-full'">
        <!-- TODO: Replace with new GoogleMaps component (see: NewEntityModal) -->
        <GMapMap
          :center="{ lat: centerLats, lng: centerLngs }"
          :zoom="zoomCenter"
          map-type-id="roadmap"
          :options="{
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
          }"
          style="position: absolute; width: 100%; height: 100%;">
          <GMapMarker
            v-for="(m, index) in markers"
            :key="index"
            :position="m.position"
            :clickable="true"
            :draggable="false"
            :label="m.label?.split(' ').map(x => x[0]).join('').slice(0, 2)"
            :title="m.label + ' (' + entitiesGrouped[m.entityIndex].titleSingle + ')'"
            @click="selectListItem(entitiesGrouped[m.entityIndex], entitiesGrouped[m.entityIndex].list[m.listItemIndex], !entitiesGrouped[m.entityIndex].active)" />
        </GMapMap>
      </div>
      <div
        v-if="selectedEntityId && selectedListItemId"
        class="relative h-full w-1/2">
        <EntityInfo
          :key="'entityInfo--' + selectedEntityId + '-' + selectedListItemId"
          :entity="selectedList"
          :entity-type="selectedEntityType"
          :entity-type-slug="selectedEntityTypeSlug"
          :destination="destination"
          :destinations="destinations"
          :current-user="currentUser"
          closable
          :has-name="{active: true, class: 'block text-xl font-medium text-cyan-600 px-2 py-0 mb-0.5 placeholder:italic'}"
          :has-description="{active: true, class: 'block text-sm font-normal px-2 py-1 -mb-1.5 placeholder:italic'}"
          :has-proposal-program-text="{active: true, class: 'block text-xs font-normal px-2 py-1 -mt-2 placeholder:italic'}"
          class="size-full border-l border-gray-200"
          v-bind="$attrs"
          @created="(newTextVersion) => updateEntitiesGrouped(newTextVersion)"
          @close="selectListItem(entitiesGrouped.find((e) => e.id === selectedEntityId), selectedList, !entitiesGrouped.find((e) => e.id === selectedEntityId).active)"
          @delete:entity="(value) => {
            selectListItem(entitiesGrouped.find((e) => e.id === selectedEntityId), selectedList, !entitiesGrouped.find((e) => e.id === selectedEntityId).active);
            deleteEntity(value.placeId, value.entityId, value.entityTypeSlug, value.entityName);
          }">
          <template #extra>
            <hr class="border-gray-200 bg-gray-200">
            <EntityNote
              class="m-4"
              :entity="selectedList"
              :route-prop="route('api.destination.entity.note.store', { entity: selectedList.id })" />
          </template>
        </EntityInfo>
      </div>
    </div>
    <NewEntityModal
      v-if="showCreateEntityModal"
      :destination="destination"
      :entities-grouped="entitiesGrouped"
      @add:entity="(value) => addNewEntityToEntitiesGrouped(value)" />
  </div>
</template>

<script setup>
/* external */
import {
  HomeIcon, CakeIcon, MapIcon, BuildingOfficeIcon, BuildingLibraryIcon, MapPinIcon, GlobeEuropeAfricaIcon, PlusSmallIcon, ExclamationCircleIcon,
} from '@heroicons/vue/24/outline';
import {
  computed, inject, onMounted, provide, ref, useAttrs,
} from 'vue';
import { notify } from 'notiwind';

/* local */
import Accordion from '@/Components/Accordion.vue';
import Tooltip from '@/Components/Tooltip.vue';
import EntityNote from '@/Pages/Destination/Components/EntityNote.vue';
import EntityInfo from '@/Pages/Destination/Components/EntityInfo.vue';
import NewEntityModal from '@/Pages/Destination/Components/DestinationCreateEntityModal.vue';
import { getCurrentLanguage } from '@/utils';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const props = defineProps({
  destination: {
    type: Object,
    required: true,
  },
  destinations: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const emit = defineEmits(['selectedEntity']);

const showCreateEntityModal = inject('showCreateEntityModal');
provide('showCreateEntityModal', showCreateEntityModal);

/**
 * Toggle the entity creation modal
 */
function toggleModal() {
  showCreateEntityModal.value = !showCreateEntityModal.value;
}

const currentLanguage = computed(() => getCurrentLanguage());
const attrs = useAttrs();
const currentUser = attrs.user;

// Entity list selection variables
const selectedEntityId = ref(null);
const selectedEntityType = ref('');
const selectedEntityTypeSlug = ref('');
const selectedListItemId = ref(null);
const selectedList = ref(null);

const availableEntities = computed(() => {
  if (props.destinations && props.destinations.length) {
    return props.destinations.reduce((acc, destination) => (destination.available_entities
      ? acc.concat(destination.available_entities)
      : acc), []);
  }

  return props.destination.available_entities;
});

/**
 * Get all entities, sort them and set properties
 */
const entitySlugsArray = ['lodging', 'eatery', 'activity', 'venue', 'landmark', 'other', 'global'];
const entitySlugsPluralArray = ['lodgings', 'eateries', 'activities', 'venues', 'landmarks', 'others', 'globals'];
const entitiesGrouped = computed(() => entitySlugsArray.map((entitySlug, index) => {
  const sortedList = availableEntities.value.filter((child) => child.type === entitySlug).map((e, i) => ({
    ...e,
    selected: false,
    position: {
      lat: e.place?.location?.coordinates[1],
      lng: e.place?.location?.coordinates[0],
    },
    global: (e.place === null && e.parent_id === null),
    position_disabled: e.place?.location?.coordinates[1] ? false : 1,
    within50km: e.place?.location?.coordinates[1]
      ? (getDistance(props.destination.location?.coordinates[1], props.destination.location?.coordinates[0], e.place?.location?.coordinates[1], e.place?.location?.coordinates[0]) <= 50)
      : null,
    label: e.place?.name ?? e.content_versions[0]?.texts[currentLanguage]?.name ?? e.content_versions[0]?.texts.en?.name,
    note: e.note,
    entityIndex: entitySlugsArray.indexOf(entitySlug),
    listItemIndex: i,
    dbSelectedVersion: (e.content_versions.length > 0
      ? (e.content_versions.find((version) => (version.owner_type === 'App\\Models\\User' && version.owner_id === currentUser.id && version.standard === true))
          ?? e.content_versions.find((version) => (version.owner_type === 'App\\Models\\Team' && version.standard === true))
          ?? e.content_versions.find((version) => version.standard === true))
        ?? e.content_versions[0] : null
    ),
  })).sort((a, b) => {
    const nameA = a.dbSelectedVersion?.texts[currentLanguage.value]?.name ?? a.place?.name ?? '';
    const nameB = b.dbSelectedVersion?.texts[currentLanguage.value]?.name ?? b.place?.name ?? '';

    return nameA.localeCompare(nameB);
  });

  return {
    id: index + 1,
    slug: entitySlug,
    title: entitySlugsPluralArray[index].charAt(0).toUpperCase() + entitySlugsPluralArray[index].slice(1),
    titleSingle: entitySlug.charAt(0).toUpperCase() + entitySlug.slice(1),
    icon: entitySlug === 'lodging' ? HomeIcon
      : entitySlug === 'eatery' ? CakeIcon
        : entitySlug === 'activity' ? MapIcon
          : entitySlug === 'venue' ? BuildingOfficeIcon
            : entitySlug === 'landmark' ? BuildingLibraryIcon
              : entitySlug === 'other' ? MapPinIcon
                : GlobeEuropeAfricaIcon,
    active: false,
    list: sortedList,
  };
}));

/**
 * Map related data setup
 */
const markers = ref([]);
const zoom = ref(10);

const centerLats = computed(() => {
  const lats = markers.value
    .filter((marker) => marker.within50km)
    .map((marker) => marker.position.lat);
  const latMax = Math.max(...lats);
  const latMin = Math.min(...lats);

  return (latMin + latMax) / 2 || props.destination.location.coordinates[1];
});

const centerLngs = computed(() => {
  const lngs = markers.value
    .filter((marker) => marker.within50km)
    .map((marker) => marker.position.lng);
  const lngMax = Math.max(...lngs);
  const lngMin = Math.min(...lngs);

  return (lngMin + lngMax) / 2 || props.destination.location.coordinates[0];
});

const zoomCenter = computed(() => {
  if (markers.value.length === 0) {
    return 13;
  }
  if (markers.value.length === 1) {
    return 16;
  }
  const lngs = markers.value
    .filter((marker) => marker.within50km)
    .map((marker) => marker.position.lng);
  const lats = markers.value
    .filter((marker) => marker.within50km)
    .map((marker) => marker.position.lat);
  const lngMax = Math.max(...lngs);
  const lngMin = Math.min(...lngs);
  const latMax = Math.max(...lats);
  const latMin = Math.min(...lats);
  const max = Math.max(latMax - latMin, lngMax - lngMin);
  const calculation = (2.7 - (max * 2)) * 5;

  return (calculation < 4) ? 4 : calculation;
});

/**
 * Search related data setup
 */
const searchQuery = ref('');

const filteredEntitiesGrouped = computed(() => {
  if (!searchQuery.value) {
    resetMarkers();

    return entitiesGrouped.value;
  }

  const filteredGroups = entitiesGrouped.value.map((group) => {
    const filteredList = group.list.filter((item) => {
      const name = item.dbSelectedVersion?.texts[currentLanguage.value]?.name ?? item.place?.name ?? '';

      return name.toLowerCase().includes(searchQuery.value.toLowerCase());
    });

    return {
      ...group, list: filteredList, active: true,
    };
  }).filter((group) => group.list.length > 0);

  // Update markers based on the filtered list
  const newMarkers = filteredGroups.flatMap((group) => {
    const entityIndex = entitiesGrouped.value.findIndex((e) => e.id === group.id);

    return group.list.map((item) => {
      const { position } = item;
      if (position && typeof position.lat === 'number' && typeof position.lng === 'number') {
        return {
          position,
          within50km: item.within50km,
          label: item.place?.name ?? item.label,
          entity: ref(group),
          listItem: ref(item),
          entityIndex,
          listItemIndex: group.list.indexOf(item),
        };
      }

      return null;
    }).filter((marker) => marker !== null);
  });

  setMarkers(newMarkers);

  return filteredGroups;
});

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * String limit function
 *
 * @param {string} value - The value
 * @param {number} size - The size
 *
 * @returns {string} - The limited string
 */
function stringLimit(value, size) {
  if (!value) {
    return '';
  }
  const text = value.toString();

  if (text.length <= size) {
    return text;
  }

  return `${text.substring(0, size)}...`;
}

/**
 * Map handling of entity markers
 *
 * @param {number} deg - The degree
 * @returns {number} - The radian value
 */
function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

/**
 * Get distance between two points in km
 *
 * @param {number} lat1 - The latitude of point 1
 * @param {number} lon1 - The longitude of point 1
 * @param {number} lat2 - The latitude of point 2
 * @param {number} lon2 - The longitude of point 2
 *
 * @returns {number} - The distance in km
 */
function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
    * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km

  return distance;
}

/**
 * Set markers on map and zoom
 *
 * @param {Array} arrayOfMarkers - The array of markers
 * @param {number} zoomValue - The zoom value
 * @param {boolean} within50km - If within 50 km state
 */
function setMarkers(arrayOfMarkers, zoomValue = 14, within50km = true) {
  if (!within50km) {
    markers.value = arrayOfMarkers.map((marker) => ({
      ...marker, within50km: true,
    }));
  } else {
    markers.value = arrayOfMarkers;
  }
  zoom.value = zoomValue;
}

/**
 * Reset markers to all entities
 */
function resetMarkers() {
  const entityMarkers = [];
  entitiesGrouped.value.forEach((entity, eI) => {
    if (entity.list.length > 0) {
      entity.list.forEach((e, lI) => {
        if (e.position_disabled) {
          return;
        }
        entityMarkers.push({
          position: e.position,
          within50km: e.within50km,
          label: e.place?.name ?? e.label,
          entity: ref(entity),
          listItem: ref(e),
          entityIndex: eI,
          listItemIndex: lI,
        });
      });
    }
  });
  markers.value = entityMarkers;
  zoom.value = 13;
}

/**
 * Toggle accordions
 *
 * @param {boolean} state - The state
 * @param {object} current - The current object
 */
function toggleAccordions(state, current) {
  if (state && current.list.length > 0) {
    if (selectedListItemId.value) {
      selectedListItemId.value = null;
    }
    setMarkers(current.list.filter((e) => (e.position_disabled !== 1 && e.within50km)));
  } else {
    resetMarkers();
  }

  entitiesGrouped.value.forEach((entity) => {
    if (entity.id === current.id) {
      entity.active = !entity.active;
    } else {
      entity.active = false;
      // we also need to reset the other entries list selected state
      if (entity.list.length > 0) {
        entity.list.forEach((e) => {
          e.selected = false;
        });
      }
    }
  });
}

/**
 * Select list item
 *
 * @param {object} entity - The entity object
 * @param {object} listItem - The list item object
 * @param {boolean} reset - Reset markers
 */
function selectListItem(entity, listItem, reset = false) {
  if (listItem.selected === true) {
    listItem.selected = false;
    selectedEntityId.value = null;
    selectedEntityType.value = '';
    selectedEntityTypeSlug.value = '';
    selectedListItemId.value = null;
    emit('selectedEntity', null);
    if (reset) {
      resetMarkers();
    } else {
      setMarkers(entity.list.filter((e) => (e.position_disabled !== 1 && e.within50km)));
    }

    return;
  }

  entity.list.forEach((e) => {
    e.selected = e.id === listItem.id ?? false;
  });

  emit('selectedEntity', listItem);

  selectedEntityId.value = entity.id;
  selectedEntityType.value = entity.titleSingle;
  selectedEntityTypeSlug.value = entity.slug;
  selectedListItemId.value = listItem.id;
  selectedList.value = entitiesGrouped.value.find((e) => e.id === entity.id).list.find((e) => e.id === listItem.id);
  if (listItem.position_disabled !== 1) {
    setMarkers([listItem], 15, (listItem.within50km ?? false));
  } else {
    setMarkers(entity.list.filter((e) => (e.position_disabled !== 1 && e.within50km)));
  }
}

/**
 * Update entitiesGrouped list with new version
 *
 * @param {object} newVersion - The new version object
 */
function updateEntitiesGrouped(newVersion) {
  // add new version to the entity list used by component
  entitiesGrouped.value.find((e) => e.id === selectedEntityId.value).list.find((e) => e.id === selectedListItemId.value).content_versions.push(newVersion);
  // also reactive current selection of the entityGroup list
  entitiesGrouped.value.find((e) => e.id === selectedEntityId.value).active = true;
  entitiesGrouped.value.find((e) => e.id === selectedEntityId.value).list.find((e) => e.id === selectedListItemId.value).selected = true;
}

/**
 * Add new entity to entitiesGrouped list and select it
 *
 * @param {object} newEntityObject - The new entity object
 */
function addNewEntityToEntitiesGrouped(newEntityObject) {
  setTimeout(() => {
    entitiesGrouped.value?.find((e) => e.slug === newEntityObject.output.type)?.list.push({
      ...newEntityObject.output,
      selected: true,
      position: newEntityObject.input.position ?? {
        lat: newEntityObject.input.location.coordinates[1], lng: newEntityObject.input.location.coordinates[0],
      },
      position_disabled: newEntityObject.input.position_disabled ?? null,
      within50km: newEntityObject.input.within50km ?? null,
      global: newEntityObject.input.global ?? null,
      label: newEntityObject.input.name,
      entityIndex: entitiesGrouped.value.findIndex((e) => e.slug === newEntityObject.output.type),
      listItemIndex: entitiesGrouped.value.find((e) => e.slug === newEntityObject.output.type).list.length,
      dbSelectedVersion: (newEntityObject.output.content_versions.length > 0
        ? (newEntityObject.output.content_versions.find((version) => (version.owner_type === 'App\\Models\\User' && version.owner_id === currentUser.id && version.standard === true))
            ?? newEntityObject.output.content_versions.find((version) => (version.owner_type === 'App\\Models\\Team' && version.standard === true))
            ?? newEntityObject.output.content_versions.find((version) => version.standard === true)) : null
      ),
    });
    // also active current selection of the entityGroup list
    entitiesGrouped.value.find((e) => e.slug === newEntityObject.output.type).active = true;
    entitiesGrouped.value.find((e) => e.slug === newEntityObject.output.type).list.find((e) => e.id === newEntityObject.output.id).selected = false;
    selectListItem(
      entitiesGrouped.value.find((e) => e.slug === newEntityObject.output.type),
      entitiesGrouped.value.find((e) => e.slug === newEntityObject.output.type).list.find((e) => e.id === newEntityObject.output.id),
    );
  }, 500);
}

/**
 * Delete entity from database and remove from list
 *
 * @param {number} placeId - The place id
 * @param {number} entityId - The entity id
 * @param {string} entityTypeSlug - The entity type slug
 * @param {string} entityName - The entity name
 */
function deleteEntity(placeId, entityId, entityTypeSlug, entityName = '') {
  // remove the element from elements array
  entitiesGrouped.value.find((e) => e.slug === entityTypeSlug)?.list.splice(entitiesGrouped.value.find((e) => e.slug === entityTypeSlug)?.list.findIndex((e) => e.id === entityId), 1);
  axios.delete(route('api.place.entity.destroy', {
    parentPlace: placeId, entity: entityId,
  })).then(() => {
    // notify user
    notify({
      type: 'success',
      title: 'Deleted',
      text: `Entity ${entityName} deleted`,
    }, 4000);
  }).catch((error) => {
    // notify user of error
    notify({
      type: 'danger',
      title: 'Something went wrong',
      text: `Could not delete entity ${entityName}`,
    }, 4000);
    // log error
    console.error('save error: ', error);
  }).finally(() => {
    // rest in peace
  });
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onMounted(() => {
  resetMarkers();
});

</script>
